import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PartySummary } from '@aeris/contracts'
import { formatDocument, partyDisplayName } from '@aeris/party'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Spinner,
  cn,
} from '@aeris/ui'

import { fetchParties } from '../lib/api.ts'

/**
 * Attaching a real customer to a counter sale, without a mouse.
 *
 * The customer is never null (F013, decision 1): the till already has the
 * tenant's `Consumidor Final` selected, and this dialog exists for the case
 * where somebody asks for a nota in their own name. Type, choose with the arrow
 * keys, Enter. Esc goes back to the sale unchanged.
 *
 * `role: 'customer'` is not decoration. A party is one registration carrying
 * roles (section 10), and putting a supplier on a sales document is a data
 * entry mistake that produces a receivable against somebody who never bought
 * anything — the server refuses it too.
 */

/**
 * Como o balcão reconhece um cliente.
 *
 * Pelo **código do sistema antigo**, antes de qualquer outra coisa. A loja usou
 * o GDOOR por dez anos e é assim que ela chama as pessoas; o CPF quase nunca
 * está cadastrado, de modo que uma lista que mostrava só o documento dizia
 * "Sem documento" oito vezes seguidas — oito linhas idênticas onde a única
 * pergunta é qual delas é o cliente certo.
 */
const identify = (party: PartySummary): string => {
  const marks = [
    party.code === undefined || party.code === null
      ? party.externalCode === null
        ? null
        : `Cód. ${party.externalCode}`
      : `Cód. ${String(party.code)}`,
    party.document === null ? null : formatDocument(party.kind, party.document),
  ].filter((mark): mark is string => mark !== null)

  return marks.length === 0 ? 'Sem código nem documento' : marks.join(' · ')
}

export type PosCustomerDialogProps = {
  onChoose: (party: PartySummary) => void
  onClose: () => void
}

export const PosCustomerDialog = ({
  onChoose,
  onClose,
}: PosCustomerDialogProps): ReactNode => {
  const [term, setTerm] = useState('')
  const [highlighted, setHighlighted] = useState(0)

  const results = useQuery({
    queryKey: ['pos-customers', term],
    queryFn: ({ signal }) =>
      fetchParties(
        {
          role: 'customer',
          status: 'active',
          pageSize: 8,
          ...(term.trim().length > 0 ? { search: term.trim() } : {}),
        },
        signal
      ),
  })

  const items = results.data?.items ?? []

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Cliente da venda"
        description="Digite o nome, o código antigo ou o documento. Use as setas e Enter para escolher."
        className="max-w-xl"
      >
        <div className="flex flex-col gap-3">
          <Field label="Buscar cliente">
            {(field) => (
              <Input
                {...field}
                value={term}
                autoFocus
                autoComplete="off"
                onChange={(event) => {
                  setTerm(event.target.value)
                  // A new search is a new list; keeping the old index would put
                  // the highlight on whatever happens to be in that position.
                  setHighlighted(0)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    setHighlighted((current) =>
                      Math.min(current + 1, Math.max(items.length - 1, 0))
                    )
                  }
                  if (event.key === 'ArrowUp') {
                    event.preventDefault()
                    setHighlighted((current) => Math.max(current - 1, 0))
                  }
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    const chosen = items[highlighted]
                    if (chosen !== undefined) onChoose(chosen)
                  }
                }}
              />
            )}
          </Field>

          {results.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum cliente encontrado.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {items.map((party, index) => (
                <li key={party.id}>
                  <button
                    type="button"
                    aria-current={index === highlighted ? 'true' : undefined}
                    className={cn(
                      'flex w-full flex-col items-start rounded-(--radius-md) px-3 py-2 text-left',
                      'text-(length:--text-sm) text-(--text-primary)',
                      // `--surface-selected`, como a linha do carrinho (F179):
                      // `--surface-raised` é o fundo do diálogo, e o cliente
                      // escolhido pelas setas sumia no tema claro (F248).
                      index === highlighted && 'bg-(--surface-selected)'
                    )}
                    onClick={() => {
                      onChoose(party)
                    }}
                  >
                    <span className="font-(--weight-medium)">
                      {partyDisplayName(party)}
                    </span>
                    <span className="text-(length:--text-xs) text-(--text-muted)">
                      {identify(party)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Voltar (Esc)
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
